/**
 * SKU 字串處理工具函數
 * 重要：保持 SKU 的精確性，包括前導零和大小寫
 * 例如："12345678" 和 "012345678" 是兩個完全不同的 SKU
 */

/**
 * 將任意值轉換為嚴格的 SKU 字串
 * 保留前導零和原始大小寫，僅去除前後空白
 * @param value 任意輸入值
 * @returns 處理後的 SKU 字串，或 null（如果輸入為空）
 */
export const toStrictSku = (value: any): string | null => {
  if (value === null || value === undefined) return null;
  const sku = String(value).trim();
  return sku === '' ? null : sku;
};

/**
 * 將任意值轉換為嚴格的 SKU 字串（非空版本）
 * 保留前導零和原始大小寫，僅去除前後空白
 * @param value 任意輸入值
 * @returns 處理後的 SKU 字串，空值返回空字串
 */
export const toStrictSkuOrEmpty = (value: any): string => {
  if (value === null || value === undefined) return '';
  const sku = String(value).trim();
  return sku;
};

/**
 * 檢查兩個 SKU 是否完全匹配
 * 進行精確的字串比較，包括前導零和大小寫
 * @param sku1 第一個 SKU
 * @param sku2 第二個 SKU
 * @returns 是否完全匹配
 */
export const isSkuExactMatch = (sku1: any, sku2: any): boolean => {
  const normalizedSku1 = toStrictSku(sku1);
  const normalizedSku2 = toStrictSku(sku2);
  
  if (normalizedSku1 === null || normalizedSku2 === null) {
    return normalizedSku1 === normalizedSku2;
  }
  
  return normalizedSku1 === normalizedSku2;
};

/**
 * 從 SKU 陣列中建立 Set 集合，用於快速查找
 * 保持 SKU 的精確性
 * @param skus SKU 陣列
 * @returns SKU Set 集合
 */
export const createSkuSet = (skus: any[]): Set<string> => {
  const skuSet = new Set<string>();
  skus.forEach(sku => {
    const strictSku = toStrictSku(sku);
    if (strictSku !== null) {
      skuSet.add(strictSku);
    }
  });
  return skuSet;
};

/**
 * 驗證 SKU 格式（可根據需求擴充）
 * @param sku SKU 字串
 * @returns 驗證結果
 */
export const validateSku = (sku: any): { isValid: boolean; message?: string } => {
  const strictSku = toStrictSku(sku);

  if (strictSku === null) {
    return { isValid: false, message: 'SKU 不能為空' };
  }

  if (strictSku.length === 0) {
    return { isValid: false, message: 'SKU 不能為空字串' };
  }

  // 可在此添加更多 SKU 格式驗證規則

  return { isValid: true };
};

/**
 * 將兩位數年份轉換為四位數年份
 * 規則：00-99 統一轉換為 2000-2099
 * @param year 年份（可能是兩位數或四位數）
 * @returns 四位數年份
 */
const normalizeYear = (year: number): number => {
  if (year < 100) {
    return 2000 + year;
  }
  return year;
};

/**
 * 判斷兩個日期是否相同（只比對到日）
 * @param d1 第一個日期
 * @param d2 第二個日期
 * @returns 是否相同
 */
export const isSameDate = (d1: Date, d2: Date): boolean => {
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
};

/**
 * 判斷三個日期是否全部相同（只比對到日）
 * @param d1 第一個日期（可為 null）
 * @param d2 第二個日期（可為 null）
 * @param d3 第三個日期（可為 null）
 * @returns 是否全部相同（若任一為 null 則返回 false）
 */
export const areAllDatesSame = (d1: Date | null, d2: Date | null, d3: Date | null): boolean => {
  if (!d1 || !d2 || !d3) return false;
  return isSameDate(d1, d2) && isSameDate(d2, d3);
};

/**
 * 從產品名稱中解析到期日期
 * 支持多種日期格式：
 * - 中文格式：2025年9月1日、2025年9月（缺日默認1日）
 * - 英文格式：1 SEP 2025、1 SEP 25、SEP 2025、SEP 25
 * - 連字符格式：1-1-2025、1-1-25、1-2025（不支援容易誤判的MM-YY格式）
 * - 標準格式：2025-09-01, 2025/09/01、1/9/2025
 * - 點號格式：2025.9.1、2025.9
 * - 月年格式：07/2027、12/2026（MM/YYYY → YYYY-MM-01）
 * - 新增格式：1/7/2025（DD/MM/YYYY）、7/25（MM/YY）
 * - Exp 標記格式：Exp:12/27、EXP：07/26（MM/YY → YYYY-MM-01，僅產品名稱中的註記，不影響 Excel 匯入）
 * - 符號包裹格式：無論被【】、（）、""等符號包裹，都按上述格式解析
 * @param productName 產品名稱
 * @returns 解析出的日期物件，如果解析失敗返回 null
 */
export const parseExpiryDate = (productName: string): Date | null => {
  if (!productName || typeof productName !== 'string') {
    return null;
  }

  // 定義多種核心日期格式（按優先順序：完整格式在前，簡化格式在後）
  const datePatterns = [
    // 1. 中文完整格式：2027年8月1日（優先級最高）
    {
      regex: /(\d{4})年(\d{1,2})月(\d{1,2})日/g,
      parser: (match: RegExpMatchArray) => {
        const [, year, month, day] = match;
        const monthNum = parseInt(month);
        const dayNum = parseInt(day);
        if (monthNum < 1 || monthNum > 12 || dayNum < 1 || dayNum > 31) return null;
        return new Date(parseInt(year), monthNum - 1, dayNum);
      }
    },
    // 2. 中文簡化格式：2027年8月（缺日，預設1日）
    {
      regex: /(\d{4})年(\d{1,2})月(?![\d日])/g,
      parser: (match: RegExpMatchArray) => {
        const [, year, month] = match;
        const monthNum = parseInt(month);
        if (monthNum < 1 || monthNum > 12) return null;
        return new Date(parseInt(year), monthNum - 1, 1); // 預設 1 日
      }
    },
    // 3. 英文月份完整格式：1 Jan 2027（支援完整日期）
    {
      regex: /(\d{1,2})\s*(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)\s*(\d{4})/gi,
      parser: (match: RegExpMatchArray) => {
        const [, day, monthStr, year] = match;
        const monthMap: { [key: string]: number } = {
          'JAN': 0, 'FEB': 1, 'MAR': 2, 'APR': 3, 'MAY': 4, 'JUN': 5,
          'JUL': 6, 'AUG': 7, 'SEP': 8, 'OCT': 9, 'NOV': 10, 'DEC': 11
        };
        const month = monthMap[monthStr.toUpperCase()];
        if (month === undefined) return null;
        return new Date(parseInt(year), month, parseInt(day));
      }
    },
    // 4. 英文月份兩位年格式：1 Jan 27
    {
      regex: /(\d{1,2})\s*(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)\s*(\d{2})\b/gi,
      parser: (match: RegExpMatchArray) => {
        const [, day, monthStr, year] = match;
        const monthMap: { [key: string]: number } = {
          'JAN': 0, 'FEB': 1, 'MAR': 2, 'APR': 3, 'MAY': 4, 'JUN': 5,
          'JUL': 6, 'AUG': 7, 'SEP': 8, 'OCT': 9, 'NOV': 10, 'DEC': 11
        };
        const month = monthMap[monthStr.toUpperCase()];
        if (month === undefined) return null;
        const normalizedYear = normalizeYear(parseInt(year));
        return new Date(normalizedYear, month, parseInt(day));
      }
    },
    // 5. 英文月份簡化格式：Jan 2027（缺日，預設1日）
    {
      regex: /\b(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)\s*(\d{4})\b/gi,
      parser: (match: RegExpMatchArray) => {
        const [, monthStr, year] = match;
        const monthMap: { [key: string]: number } = {
          'JAN': 0, 'FEB': 1, 'MAR': 2, 'APR': 3, 'MAY': 4, 'JUN': 5,
          'JUL': 6, 'AUG': 7, 'SEP': 8, 'OCT': 9, 'NOV': 10, 'DEC': 11
        };
        const month = monthMap[monthStr.toUpperCase()];
        if (month === undefined) return null;
        return new Date(parseInt(year), month, 1); // 預設 1 日
      }
    },
    // 6. 英文月份兩位年簡化格式：Jan 27（缺日，預設1日）
    {
      regex: /\b(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)\s*(\d{2})\b/gi,
      parser: (match: RegExpMatchArray) => {
        const [, monthStr, year] = match;
        const monthMap: { [key: string]: number } = {
          'JAN': 0, 'FEB': 1, 'MAR': 2, 'APR': 3, 'MAY': 4, 'JUN': 5,
          'JUL': 6, 'AUG': 7, 'SEP': 8, 'OCT': 9, 'NOV': 10, 'DEC': 11
        };
        const month = monthMap[monthStr.toUpperCase()];
        if (month === undefined) return null;
        const normalizedYear = normalizeYear(parseInt(year));
        return new Date(normalizedYear, month, 1); // 預設 1 日
      }
    },
    // 7. YYYY/MM/DD 格式
    {
      regex: /(\d{4})\/(\d{1,2})\/(\d{1,2})/g,
      parser: (match: RegExpMatchArray) => {
        const [, year, month, day] = match;
        const monthNum = parseInt(month);
        const dayNum = parseInt(day);
        if (monthNum < 1 || monthNum > 12 || dayNum < 1 || dayNum > 31) return null;
        return new Date(parseInt(year), monthNum - 1, dayNum);
      }
    },
    // 8. DD/MM/YYYY 格式
    {
      regex: /(\d{1,2})\/(\d{1,2})\/(\d{2,4})/g,
      parser: (match: RegExpMatchArray) => {
        const [, day, month, year] = match;
        const monthNum = parseInt(month);
        const dayNum = parseInt(day);
        if (monthNum < 1 || monthNum > 12 || dayNum < 1 || dayNum > 31) return null;
        const normalizedYear = normalizeYear(parseInt(year));
        return new Date(normalizedYear, monthNum - 1, dayNum);
      }
    },
    // 9. YYYY-MM-DD 格式
    {
      regex: /(\d{4})-(\d{1,2})-(\d{1,2})/g,
      parser: (match: RegExpMatchArray) => {
        const [, year, month, day] = match;
        const monthNum = parseInt(month);
        const dayNum = parseInt(day);
        if (monthNum < 1 || monthNum > 12 || dayNum < 1 || dayNum > 31) return null;
        return new Date(parseInt(year), monthNum - 1, dayNum);
      }
    },
    // 10. DD-MM-YYYY 格式
    {
      regex: /\b(\d{1,2})-(\d{1,2})-(\d{2,4})\b/g,
      parser: (match: RegExpMatchArray) => {
        const [, day, month, year] = match;
        const monthNum = parseInt(month);
        const dayNum = parseInt(day);
        if (monthNum < 1 || monthNum > 12 || dayNum < 1 || dayNum > 31) return null;
        const normalizedYear = normalizeYear(parseInt(year));
        return new Date(normalizedYear, monthNum - 1, dayNum);
      }
    },
    // 11. YYYY.MM.DD 格式
    {
      regex: /(\d{4})\.(1[0-2]|0?[1-9])\.([12]\d|3[01]|0?[1-9])/g,
      parser: (match: RegExpMatchArray) => {
        const [, year, month, day] = match;
        return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      }
    },
    // 12. YYYY.MM 格式（缺日，預設1日）
    {
      regex: /(\d{4})\.(1[0-2]|0?[1-9])(?!\.)/g,
      parser: (match: RegExpMatchArray) => {
        const [, year, month] = match;
        return new Date(parseInt(year), parseInt(month) - 1, 1); // 預設 1 日
      }
    },
    // 13. DD.MM.YYYY 格式
    {
      regex: /([12]\d|3[01]|0?[1-9])\.(1[0-2]|0?[1-9])\.(\d{4})/g,
      parser: (match: RegExpMatchArray) => {
        const [, day, month, year] = match;
        return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      }
    },
    // 14. MM.YYYY 格式（只支援四位數年份，缺日，預設1日）
    {
      regex: /(1[0-2]|0?[1-9])\.(\d{4})(?!\.)/g,
      parser: (match: RegExpMatchArray) => {
        const [, month, year] = match;
        return new Date(parseInt(year), parseInt(month) - 1, 1); // 預設 1 日
      }
    },
    // 15. MM/YYYY 格式（只支援四位數年份）
    {
      regex: /\b(\d{1,2})\/(\d{4})\b(?!\d)/g,
      parser: (match: RegExpMatchArray) => {
        const [, month, year] = match;
        const monthNum = parseInt(month);
        if (monthNum < 1 || monthNum > 12) return null;
        return new Date(parseInt(year), monthNum - 1, 1); // 預設 1 日
      }
    },
    // 16. MM-YYYY 格式（只支援四位數年份）
    {
      regex: /\b(\d{1,2})-(\d{4})\b(?!\d)/g,
      parser: (match: RegExpMatchArray) => {
        const [, month, year] = match;
        const monthNum = parseInt(month);
        if (monthNum < 1 || monthNum > 12) return null;
        return new Date(parseInt(year), monthNum - 1, 1); // 預設 1 日
      }
    },
    // 17. Exp:MM/YY 或 EXP：MM/YY 格式（只支援兩位數年份，日固定為 1）
    // 說明：這個格式只會出現在產品名稱中，例如：
    // - "ABC SNACK Exp:12/27"
    // - "ABC 零食 EXP：07/26"
    // 不影響 Excel 匯入欄位的日期處理。
    {
      regex: /\bEXP?[:：]\s*(\d{1,2})\/(\d{2})\b/gi,
      parser: (match: RegExpMatchArray) => {
        const [, month, year] = match;
        const monthNum = parseInt(month, 10);
        if (monthNum < 1 || monthNum > 12) return null;
        const normalizedYear = normalizeYear(parseInt(year, 10));
        // 規則：Exp:12/27 => 01/12/2027
        return new Date(normalizedYear, monthNum - 1, 1);
      }
    }
  ];

  // 嘗試每種日期格式（無論被什麼符號包裹，都會被正則表達式匹配）
  for (const { regex, parser } of datePatterns) { 
    const matches = [...productName.matchAll(regex)];
    for (const match of matches) {
      const date = parser(match);
      if (date && !isNaN(date.getTime())) {
        return date;
      }
    }
  }

  return null;
};

/**
 * 計算日期之間的天數差
 * @param date1 第一個日期
 * @param date2 第二個日期（預設為今天）
 * @returns 天數差（正數表示 date1 在 date2 之後）
 */
export const getDaysDifference = (date1: Date, date2: Date = new Date()): number => {
  const timeDiff = date1.getTime() - date2.getTime();
  return Math.ceil(timeDiff / (1000 * 3600 * 24));
};

/**
 * 檢查產品是否在指定天數內到期
 * @param productName 產品名稱
 * @param days 檢查的天數範圍
 * @returns 如果在範圍內到期，返回剩餘天數；否則返回 null
 */
export const getExpiryDays = (productName: string, days: number = 30): number | null => {
  const expiryDate = parseExpiryDate(productName);
  if (!expiryDate) return null;

  const daysRemaining = getDaysDifference(expiryDate);
  return daysRemaining >= 0 && daysRemaining <= days ? daysRemaining : null;
};
